/**
 * Tabela de clientes: cabeçalhos ordenáveis, linhas que deslizam para a nova
 * posição ao filtrar/ordenar (FLIP do framer-motion) e, no celular, cartão.
 * Setas ↑/↓ andam pelas linhas; Enter abre a ficha.
 */
import React, { useCallback, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiChevronRight } from "react-icons/fi";
import { Link } from "react-router-dom";

import { formatarData } from "../../../../utils/calculos";
import { formatarReaisBR, plural } from "../../../../utils/dinheiro";
import { AnelDeSaude } from "../comum/Graficos";
import { CURVA_ENTRADA, CURVA_SAIDA, DURACAO, atrasoEmCascata } from "../comum/movimento";
import { ORDENS } from "../dados/filtros";
import { ROTULO_DA_ESPERA, ROTULO_DA_FAIXA } from "../dados/regras";

const POR_PAGINA = 40;
const OCULTO = "••••••";
const ICONE_DA_ESPERA = { equipe: "!", cliente: "…", ninguem: "✓", indefinido: "–" };

const COLUNAS = [
  { ordem: ORDENS.SAUDE, rotulo: "Saúde" },
  { ordem: ORDENS.NOME, rotulo: "Cliente" },
  { rotulo: "Etapa" },
  { ordem: ORDENS.AMBIENTES, rotulo: "Ambientes" },
  { ordem: ORDENS.ENTREGA, rotulo: "Próxima entrega" },
  { rotulo: "Quem espera", daCentral: true },
  { ordem: ORDENS.PENDENCIAS, rotulo: "Pend.", daCentral: true },
  { rotulo: "Valor" },
  { rotulo: "" },
];

function iniciais(nome) {
  return String(nome).split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

function Cabecalho({ ordem, direcao, aoOrdenar, comCentral }) {
  return (
    <div className="cl-thead" role="row">
      {COLUNAS.map((c, i) => {
        if (c.daCentral && !comCentral) return <span key={c.rotulo} aria-hidden="true" />;
        if (!c.ordem) return <span key={c.rotulo || i} className="cl-th" role="columnheader">{c.rotulo}</span>;
        const ativa = ordem === c.ordem;
        const sentido = ativa ? (direcao === 1 ? "ascending" : "descending") : "none";
        return (
          <span key={c.rotulo} role="columnheader" aria-sort={sentido}>
            <button
              type="button"
              className="cl-th"
              aria-label={`Ordenar por ${c.rotulo}`}
              onClick={() => aoOrdenar(c.ordem)}
            >
              {c.rotulo}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                style={{ transform: ativa && direcao === -1 ? "rotate(180deg)" : "none" }}>
                <path d={ativa ? "m6 9 6 6 6-6" : "m7 15 5 5 5-5M7 9l5-5 5 5"} />
              </svg>
            </button>
          </span>
        );
      })}
    </div>
  );
}

function Espera({ espera }) {
  return (
    <span className={`cl-wait ${espera}`}>
      <i aria-hidden="true">{ICONE_DA_ESPERA[espera]}</i>
      {ROTULO_DA_ESPERA[espera]}
    </span>
  );
}

function Prazo({ linha }) {
  const data = linha.proximaEntrega ? formatarData(linha.proximaEntrega) : "—";
  const detalhe = linha.atrasoAcumulado > 0 && (linha.diasProximaEntrega ?? -1) >= 0
    ? `${linha.prazo} · ${linha.atrasoAcumulado}d acum.`
    : linha.prazo;
  return (
    <span className={`cl-due ${linha.tomPrazo}`}>
      <b>{data}</b>
      <span title={detalhe}>{detalhe}</span>
    </span>
  );
}

function Progresso({ linha, atraso }) {
  const total = Math.max(1, linha.totalAmbientes);
  return (
    <>
      <motion.span
        className="cl-prog"
        style={{ transformOrigin: "left center" }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: DURACAO.barras, ease: CURVA_ENTRADA, delay: atraso + 0.1 }}
        role="img"
        aria-label={`${linha.entregues} de ${linha.totalAmbientes} ambientes entregues, ${linha.emAndamento} em andamento`}
      >
        {linha.entregues > 0 && <i className="cl-seg-e" style={{ flex: linha.entregues / total }} />}
        {linha.emAndamento > 0 && <i className="cl-seg-n" style={{ flex: linha.emAndamento / total }} />}
      </motion.span>
      <span className="cl-prog-l" aria-hidden="true">
        <span><b>{linha.entregues}</b>/{linha.totalAmbientes} entregues</span>
        <span>{linha.emAndamento} em andamento</span>
      </span>
    </>
  );
}

function Linha({ linha, indice, comCentral, precosVisiveis, aoAbrir, destacada }) {
  const atraso = atrasoEmCascata(indice, 0.035);
  const valor = precosVisiveis ? formatarReaisBR(linha.valor) : OCULTO;
  return (
    <motion.div
      layout="position"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0, transition: { duration: DURACAO.componente, ease: CURVA_ENTRADA, delay: atraso } }}
      exit={{ opacity: 0, scale: 0.98, transition: { duration: 0.16, ease: CURVA_SAIDA } }}
      transition={{ layout: { duration: DURACAO.componente, ease: CURVA_ENTRADA } }}
    >
      <Link
        to={`/legado/clientes/${linha.id}`}
        className={`cl-row${linha.atrasado ? " late" : ""}`}
        data-linha={linha.id}
        data-vt-destaque={destacada ? "" : undefined}
        aria-label={`${linha.nome}, saúde ${linha.saude}% (${ROTULO_DA_FAIXA[linha.faixa]}), ${linha.prazo}`}
        onClick={(evento) => aoAbrir(evento, linha.id)}
      >
        <span className="c-h" data-vt="anel">
          <AnelDeSaude saude={linha.saude} faixa={linha.faixa} atraso={atraso + 0.1} />
        </span>
        <span className="c-c">
          <span className="cl-nm" data-vt="nome">{linha.nome}</span>
          <span className="cl-meta">
            {linha.pedidos.map((p) => <span key={p} className="cl-ped">#{p}</span>)}
            <span>{plural(linha.totalPedidos, "pedido", "pedidos")} · {linha.totalAmbientes} amb.</span>
            {linha.projetista && (
              <span className="cl-prj"><i aria-hidden="true">{iniciais(linha.projetista)}</i>{linha.projetista.split(" ")[0]}</span>
            )}
          </span>
        </span>
        <span className="c-e"><span className={`cl-pill p-${linha.etapa.tom}`} title={linha.etapa.rotulo}><span>{linha.etapa.rotulo}</span></span></span>
        <span className="c-a"><Progresso linha={linha} atraso={atraso} /></span>
        <span className="c-d"><Prazo linha={linha} /></span>
        <span className="c-w">{comCentral && <Espera espera={linha.espera} />}</span>
        <span className="c-p">
          {comCentral && <span className={`cl-pn${linha.pendencias ? " has" : ""}`} title="Pendências abertas">{linha.pendencias}</span>}
        </span>
        <span className="c-v"><span className={`cl-val${precosVisiveis ? "" : " priv"}`}>{valor}</span></span>
        <span className="c-m">
          <span className={`cl-pill sm p-${linha.etapa.tom}`}><span>{linha.etapa.rotulo}</span></span>
          {comCentral && <Espera espera={linha.espera} />}
          {comCentral && linha.pendencias > 0 && <span className="cl-pn has">{plural(linha.pendencias, "pend.", "pend.")}</span>}
          <span className={`cl-mdue ${linha.tomPrazo}`}>
            {linha.proximaEntregaLabel || "—"} · {linha.prazo}
          </span>
        </span>
        <span className="c-x cl-go" aria-hidden="true"><FiChevronRight size={16} /></span>
      </Link>
    </motion.div>
  );
}

/** ↑/↓ andam pelas linhas visíveis. */
function navegarComSetas(evento) {
  if (evento.key !== "ArrowDown" && evento.key !== "ArrowUp") return;
  const linhas = [...evento.currentTarget.querySelectorAll("[data-linha]")];
  const atual = linhas.indexOf(document.activeElement);
  if (atual < 0) return;
  evento.preventDefault();
  const proxima = linhas[atual + (evento.key === "ArrowDown" ? 1 : -1)];
  proxima?.focus();
}

export default function TabelaDeClientes({
  linhas, situacaoRotulo, ordem, direcao, aoOrdenar, comCentral, precosVisiveis, aoAbrir, destacadaId, aoLimpar,
  animarEntrada = true,
}) {
  const [limite, setLimite] = useState(POR_PAGINA);
  const visiveis = linhas.slice(0, limite);
  const verMais = useCallback(() => setLimite((n) => n + POR_PAGINA), []);

  return (
    <div className="cl-tbl" role="table" aria-label="Clientes" aria-rowcount={linhas.length} data-pdf="tabela">
      <Cabecalho ordem={ordem} direcao={direcao} aoOrdenar={aoOrdenar} comCentral={comCentral} />
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className="cl-rows" onKeyDown={navegarComSetas}>
        <AnimatePresence initial={animarEntrada} mode="popLayout">
          {visiveis.map((linha, i) => (
            <Linha
              key={linha.id}
              linha={linha}
              indice={i}
              comCentral={comCentral}
              precosVisiveis={precosVisiveis}
              aoAbrir={aoAbrir}
              destacada={linha.id === destacadaId}
            />
          ))}
        </AnimatePresence>
        {linhas.length === 0 && (
          <motion.div className="cl-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <b>Nenhum cliente encontrado</b>
            Ajuste a busca ou limpe os filtros.
            <button type="button" className="cl-btn sm" onClick={aoLimpar}>Limpar filtros</button>
          </motion.div>
        )}
      </div>
      <div className="cl-tfoot">
        <span>
          Mostrando <b>{visiveis.length}</b> de <b>{linhas.length}</b> {situacaoRotulo}
        </span>
        {visiveis.length < linhas.length && (
          <button type="button" className="cl-btn sm" onClick={verMais}>Carregar mais</button>
        )}
      </div>
    </div>
  );
}
